import { categories } from '@/mocks/categories';

type SchemaType =
  | 'Organization'
  | 'WebSite'
  | 'WebPage'
  | 'BreadcrumbList'
  | 'Article'
  | 'FAQPage';

interface SchemaOrgProps {
  type: SchemaType;
  data?: Record<string, unknown>;
}

export function generateWebsiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'NTR 深度解析与完整指南',
    url: 'https://ntrinsight.github.io',
    description:
      '全面介绍NTR的定义、起源、文化背景、常见场景及相关讨论，帮助读者快速、系统地了解NTR的核心概念与延伸知识，是探索NTR话题的权威参考页面。',
    inLanguage: 'zh-CN',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://ntrinsight.github.io/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };
}

export function generateOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'NTR 深度解析',
    url: 'https://ntrinsight.github.io',
    description:
      '致力于为中文用户提供最全面、最深入的NTR文化知识，帮助每一位读者系统性地理解这一独特的ACG文化现象。',
  };
}

export function generateBreadcrumbSchema(
  items: { name: string; url: string }[],
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

export function generateArticleSchema(article: {
  title: string;
  summary: string;
  image: string;
  date: string;
  slug: string;
  tags: string[];
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    image: article.image,
    datePublished: article.date,
    dateModified: article.date,
    url: `https://ntrinsight.github.io/article/${article.slug}`,
    author: {
      '@type': 'Organization',
      name: 'NTR 深度解析',
    },
    publisher: {
      '@type': 'Organization',
      name: 'NTR 深度解析',
    },
    inLanguage: 'zh-CN',
    keywords: article.tags.join(', '),
  };
}

export function generateFaqSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'NTR是什么意思？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'NTR是日语"寝取られ"（Netorare）的缩写，源自日本ACG文化，指伴侣或心仪对象被他人夺走的情节设定。在ACG语境中，NTR通常描述恋爱关系中一方被第三方夺走的故事情节。',
        },
      },
      {
        '@type': 'Question',
        name: 'NTR的起源是什么？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'NTR概念起源于日本1980-1990年代的同人创作文化，在PC游戏时代逐渐成型，2000年代通过互联网论坛固化，继而传播至全球ACG社群。',
        },
      },
      {
        '@type': 'Question',
        name: 'NTR与BSS有什么区别？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'NTR指已有恋爱关系被破坏，伴侣被第三方夺走；BSS（僕が先に好きだったのに）指尚未建立关系时，心仪对象被他人抢先。两者的核心差异在于是否存在已确立的恋爱关系。',
        },
      },
      {
        '@type': 'Question',
        name: '为什么NTR题材会吸引特定受众？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'NTR吸引特定受众的原因包括：虚构作品提供的情感安全距离、强烈的代入感与沉浸体验、负面情绪的审美化处理（情感净化），以及"可控的失控"带来的特殊心理体验。',
        },
      },
      {
        '@type': 'Question',
        name: 'NTR文化主要在哪些媒介中流行？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'NTR文化主要在三大媒介中流行：游戏（视觉小说与GalGame是概念诞生地与主要载体）、漫画（同人与商业漫画均有大量NTR作品）、动画（OVA领域为主要阵地，商业动画也有涉及）。',
        },
      },
    ],
  };
}

export default function SchemaOrg({ type, data }: SchemaOrgProps) {
  let schema: Record<string, unknown>;

  switch (type) {
    case 'Organization':
      schema = generateOrganizationSchema();
      break;
    case 'WebSite':
      schema = generateWebsiteSchema();
      break;
    case 'BreadcrumbList':
      schema = generateBreadcrumbSchema(
        (data?.items as { name: string; url: string }[]) || [],
      );
      break;
    case 'Article':
      schema = generateArticleSchema(
        data as unknown as {
          title: string;
          summary: string;
          image: string;
          date: string;
          slug: string;
          tags: string[];
        },
      );
      break;
    case 'FAQPage':
      schema = generateFaqSchema();
      break;
    default:
      schema = {};
  }

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}